import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Plus, Edit, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ManageAssociatesPage = () => {
  const navigate = useNavigate();
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    institution_name: 'Eptral.Hospital Healthcare Solutions',
    password: ''
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'CRM Officer') {
      toast.error('Only CRM Officers can manage associates');
      navigate('/crm');
      return;
    }
    fetchAssociates();
  }, [navigate]);

  const fetchAssociates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/associates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssociates(response.data);
    } catch (error) {
      toast.error('Failed to fetch associates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (associate = null) => {
    if (associate) {
      setEditMode(true);
      setSelectedAssociate(associate);
      setFormData({
        full_name: associate.full_name,
        email: associate.email,
        phone_number: associate.phone_number,
        institution_name: associate.institution_name,
        password: ''
      });
    } else {
      setEditMode(false);
      setSelectedAssociate(null);
      setFormData({
        full_name: '',
        email: '',
        phone_number: '',
        institution_name: 'Eptral.Hospital Healthcare Solutions',
        password: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.phone_number) {
      toast.error('All fields are required');
      return;
    }

    if (!editMode && !formData.password) {
      toast.error('Password is required for new associates');
      return;
    }

    setFormLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editMode) {
        await axios.put(
          `${API}/crm/associates/${selectedAssociate.id}`,
          {
            full_name: formData.full_name,
            email: formData.email,
            phone_number: formData.phone_number
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Associate updated successfully');
      } else {
        await axios.post(
          `${API}/crm/associates`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Associate created successfully');
      }
      
      setDialogOpen(false);
      fetchAssociates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save associate');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = async (associateId) => {
    if (!window.confirm('Are you sure you want to deactivate this associate?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crm/associates/${associateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Associate deactivated successfully');
      fetchAssociates();
    } catch (error) {
      toast.error('Failed to deactivate associate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading associates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Manage CRM Associates</h1>
            <Button onClick={() => navigate('/crm')} variant="outline" className="border-[#0056B3] text-[#0056B3]">
              Back to CRM
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl p-6 shadow-xl">
          {/* Header with Add Button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>CRM Associates</h2>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-associate-btn"
                  onClick={() => handleOpenDialog()}
                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Associate
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editMode ? 'Edit Associate' : 'Add New Associate'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email"
                      disabled={editMode}
                    />
                  </div>
                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div>
                    <Label>Institution Name</Label>
                    <Input
                      value={formData.institution_name}
                      onChange={(e) => setFormData({ ...formData, institution_name: e.target.value })}
                      placeholder="Enter institution"
                    />
                  </div>
                  {!editMode && (
                    <div>
                      <Label>Password *</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter password"
                      />
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={formLoading} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                      {formLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</>
                      ) : (
                        editMode ? 'Update' : 'Create'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Associates Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Institution</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {associates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No associates found
                    </TableCell>
                  </TableRow>
                ) : (
                  associates.map((associate) => (
                    <TableRow key={associate.id}>
                      <TableCell className="font-medium">{associate.full_name}</TableCell>
                      <TableCell>{associate.email}</TableCell>
                      <TableCell>{associate.phone_number}</TableCell>
                      <TableCell>{associate.institution_name}</TableCell>
                      <TableCell>
                        <Badge className={associate.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {associate.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(associate)}
                            data-tooltip="Edit Associate"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(associate.id)}
                            data-tooltip="Deactivate Associate"
                            disabled={!associate.is_active}
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManageAssociatesPage;